/**
 * CRM-002 Recommendations — work a tech proposed that the customer didn't
 * buy that day. These are STATED facts (who proposed what, when, for how
 * much) — never blended with AI suggestions ([[feedback_statistical_vs_stated]];
 * the right-rail "Pegasus recommends" SOON card is the AI lane).
 *
 * Lifecycle: open → booked / declined / expired (quoted lands with the
 * proposal bridge). Resolved rows keep their audit trail; nothing is deleted.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export interface Recommendation {
  id: string;
  custno: string;
  locno: string | null;
  source_dispatch: string | null;
  tech_empno: string | null;
  title: string;
  detail: string | null;
  est_price: number | null;
  reaction: "considering" | "declined_today" | "asked_for_quote" | null;
  status: "open" | "quoted" | "booked" | "declined" | "expired";
  created_by: string | null;
  created_at: string;
  resolved_at: string | null;
  resolved_by: string | null;
  source: "manual" | "ai";
}

/** Plain words for the customer's reaction (dispatcher vocabulary).
 *  Rendered as "· customer {label}" — keep each label grammatical there. */
export const REACTION_LABEL: Record<NonNullable<Recommendation["reaction"]>, string> = {
  considering: "is thinking about it",
  declined_today: "said no for now",
  asked_for_quote: "wants a quote",
};

export function useRecommendations(custno: string) {
  return useQuery({
    queryKey: ["recommendations", custno],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<Recommendation[]> => {
      const { data, error } = await supabase
        .from("pegasus_recommendation")
        .select("*")
        .eq("custno", custno)
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as Recommendation[];
    },
  });
}

export function useAddRecommendation(custno: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      locno?: string | null;
      title: string;
      detail?: string | null;
      est_price?: number | null;
      tech_empno?: string | null;
      reaction?: Recommendation["reaction"];
      source_dispatch?: string | null;
    }) => {
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase.from("pegasus_recommendation").insert({
        custno,
        locno: v.locno ?? null,
        title: v.title.trim(),
        detail: v.detail?.trim() || null,
        est_price: v.est_price ?? null,
        tech_empno: v.tech_empno || null,
        reaction: v.reaction ?? null,
        source_dispatch: v.source_dispatch ?? null,
        created_by: u?.user?.email ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["recommendations", custno] }),
  });
}

export function useSetRecommendationStatus(custno: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; status: Recommendation["status"] }) => {
      const { data: u } = await supabase.auth.getUser();
      const resolved = v.status !== "open";
      const { error } = await supabase
        .from("pegasus_recommendation")
        .update({
          status: v.status,
          resolved_at: resolved ? new Date().toISOString() : null,
          resolved_by: resolved ? (u?.user?.email ?? null) : null,
        })
        .eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["recommendations", custno] }),
  });
}
